import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { JobListing } from 'job-bank-data-access';
import { assetBaseUrl } from '../asset-base-url';
import { formatStartDate } from '../listing-format';
import './DirectApplyPage.css';

export interface DirectApplyPageProps {
  listing: JobListing;
  // Advances to ResumeUploadPage (spec 011). Answers on this screen are
  // never sent to the BFF; see docs/specs/009-direct-apply-screening-
  // questions.md.
  onContinue: () => void;
  onBack: () => void;
}

type Answer = 'yes' | 'no';

const TOTAL_STEPS = 3;
const CURRENT_STEP = 1;

// All 12 BFF fixtures use workArrangement of the form
// '[Employment type] · On-site' | '· Hybrid' | '· Remote' -- a controlled
// vocabulary, so a substring check is stable across the whole pool.
function needsWorkArrangementQuestion(workArrangement: string): boolean {
  return workArrangement.includes('On-site') || workArrangement.includes('Hybrid');
}

export function DirectApplyPage({ listing, onContinue, onBack }: DirectApplyPageProps) {
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [startDateAnswer, setStartDateAnswer] = useState<Answer | null>(null);
  const [workArrangementAnswer, setWorkArrangementAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const title = locale === 'fr' ? listing.title.fr : listing.title.en;
  const showWorkArrangementQuestion = needsWorkArrangementQuestion(listing.workArrangement);

  const startDateQuestion = listing.startDate
    ? t('directApply.questions.startDate', {
        date: formatStartDate(listing.startDate, locale, t, 'jobDetails'),
      })
    : t('directApply.questions.startDateAsap');

  const allAnswered =
    startDateAnswer !== null && (!showWorkArrangementQuestion || workArrangementAnswer !== null);

  return (
    <div className="direct-apply">
      <scds-progress-bar
        current={CURRENT_STEP}
        total={TOTAL_STEPS}
        step-label={t('directApply.heading')}
        label={t('directApply.progressLabel')}
      />

      <section className="direct-apply__heading">
        <h1 ref={headingRef} tabIndex={-1} className="direct-apply__h1">
          {t('directApply.heading')}
        </h1>
        <div aria-hidden="true" className="direct-apply__accent-bar" />
      </section>

      <div className="direct-apply__job-card">
        <div className="direct-apply__job-icon" aria-hidden="true">
          <scds-icon name="briefcase" size="md" />
        </div>
        <div className="direct-apply__job-body">
          <p className="direct-apply__job-eyebrow">{t('directApply.applyingFor')}</p>
          <p className="direct-apply__job-title">{title}</p>
          <p className="direct-apply__job-meta">
            {listing.employer} &middot; {listing.city}, {listing.province}
          </p>
        </div>
      </div>

      <div className="direct-apply__questions">
        <fieldset className="direct-apply__question">
          <legend className="direct-apply__question-label">{startDateQuestion}</legend>
          <div className="direct-apply__choice-group">
            {(['yes', 'no'] as const).map((value) => (
              <label
                key={value}
                className={`direct-apply__choice${
                  startDateAnswer === value ? ' direct-apply__choice--selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="startDateAvailability"
                  value={value}
                  checked={startDateAnswer === value}
                  onChange={() => setStartDateAnswer(value)}
                />
                <span aria-hidden="true" className="direct-apply__choice-dot" />
                {t(`directApply.answers.${value}`)}
              </label>
            ))}
          </div>
          {startDateAnswer === 'no' && (
            <scds-notice notice-title={t('directApply.notices.startDate')} tone="warning" title-tag="h3" />
          )}
        </fieldset>

        {showWorkArrangementQuestion && (
          <fieldset className="direct-apply__question">
            <legend className="direct-apply__question-label">
              {t('directApply.questions.workArrangement')}
            </legend>
            <div className="direct-apply__choice-group">
              {(['yes', 'no'] as const).map((value) => (
                <label
                  key={value}
                  className={`direct-apply__choice${
                    workArrangementAnswer === value ? ' direct-apply__choice--selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="workArrangementAvailability"
                    value={value}
                    checked={workArrangementAnswer === value}
                    onChange={() => setWorkArrangementAnswer(value)}
                  />
                  <span aria-hidden="true" className="direct-apply__choice-dot" />
                  {t(`directApply.answers.${value}`)}
                </label>
              ))}
            </div>
            {workArrangementAnswer === 'no' && (
              <scds-notice notice-title={t('directApply.notices.workArrangement')} tone="warning" title-tag="h3" />
            )}
          </fieldset>
        )}
      </div>

      <div className="direct-apply__footer">
        <button type="button" className="direct-apply__footer-back" onClick={onBack}>
          <span>&#8249;</span> {t('directApply.back')}
        </button>
        <button
          type="button"
          className="direct-apply__continue"
          aria-disabled={!allAnswered}
          onClick={() => {
            if (allAnswered) onContinue();
          }}
        >
          {t('directApply.continue')}
        </button>
      </div>
    </div>
  );
}
